import { inject, Injectable } from '@angular/core';
import { RootServicesModule } from './root-services.module';
import {
    IUploadedFile,
    IUploadPreview,
    IInformationManagement,
    IInformationLegal,
    IInformationLocation,
    IInformationTranslation,
    IFolder
} from '@app-root-interfaces';
import { IUploadFileForm } from '../interfaces/upload-file-form.interface';
import { of, Observable, range, throwError, Observer } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@app-environment';
import { switchMap, map } from 'rxjs/operators';
import { InformationType } from '../enums/information-type.enum';
import { ISignedURL } from '@scf-monorepo/interfaces';
import { SignedURLDto } from '@scf-monorepo/backend-lib/dtos';

@Injectable({
    providedIn: RootServicesModule
})
export class UploadService {
    private http = inject(HttpClient);

    public createUpdatedFile(original: IUploadedFile, formData: IUploadFileForm) {
        const originalCopy: IUploadedFile = {
            ...original,
            metaInfo: { ...original.metaInfo },
            parentDrive: original.parentDrive ? { ...original.parentDrive } : undefined,
            parentFolders: [...(original.parentFolders || [])]
        };
        let orgUnits = [];
        originalCopy.titles = formData.titles;
        originalCopy.subtitle = formData.subtitle;
        originalCopy.languages = formData.language;
        originalCopy.metaInfo.owner = formData.author;
        originalCopy.metaInfo.version = formData.version;
        originalCopy.metaInfo.size = original.size;
        if (formData.parentDrive) {
            originalCopy.parentDrive = {
                id: formData.parentDrive.id
            };
            /*       originalCopy.parentDrive.orgUnit = formData.parentDrive.orgUnit.join(' ');
      originalCopy.parentDrive.filePriority = formData.parentDrive.filePriority;
      originalCopy.parentDrive.id = formData.parentDrive.id; */
            orgUnits = [...formData.parentDrive.orgUnit];
        }
        if (formData.parentFolders) {
            originalCopy.parentFolders = formData.parentFolders.map((folder) => {
                folder.orgUnit.map((unit) => {
                    if (orgUnits.indexOf(unit) < 0) {
                        orgUnits.push(unit);
                    }
                });
                return {
                    ...folder,
                    orgUnit: folder.orgUnit
                };
            });
        }
        return of(originalCopy);
    }

    public createSignedUrl(file: IUploadedFile, _drive: IFolder): Observable<{ signedUrl: string }> {
        const payload = {
            id: file.id,
            name: file.name,
            mimeType: file.type,
            owner: file.updatedBy.name
        };
        return this.http.post<{ signedUrl: string }>(`${environment.gatewayAPI}/uploads/files`, payload);
    }

    public createPreviewSignedUrl(
        file: IUploadedFile,
        _drive: IFolder
    ): Observable<{ signedUrl: string; locationUrl: string }> {
        const payload = {
            id: file.id,
            name: file.preview.name,
            mimeType: file.preview.type
        };
        return this.http.post<{ signedUrl: string; locationUrl: string }>(
            `${environment.gatewayAPI}/uploads/previews`,
            payload
        );
    }

    public uploadFile(url: string, filePath: string): Observable<any> {
        return this.http
            .get(filePath, { responseType: 'blob' as const })
            .pipe(switchMap((file: Blob) => this.http.put<any>(url, file)));
    }

    public uploadPreview(url: string, preview: IUploadPreview): Observable<any> {
        return this.http
            .get(preview.url, { responseType: 'blob' as const })
            .pipe(switchMap((blob: Blob) => this.http.put<any>(url, blob)));
    }

    public parseManagementInformations(files: File[]): Observable<IInformationManagement[]> {
        const length = files.length;
        return range(0, length).pipe(
            map((index) => files[index]),
            switchMap((file) => this.readFile(file)),
            map((payload) => this.parseDelimitedCsv(payload)),
            map((informations) =>
                informations.map((information) => ({
                    organisation: information.o || '',
                    country: information.c || '',
                    orgUnit: information.OrgCode || '',
                    language: information.language || '',
                    managementLine: information.management_line || '',
                    orgLevel1: information.Org_Level1 || '',
                    orgLevel2: information.Org_Level2 || '',
                    orgLevelRest: information.Org_LevelRest || ''
                }))
            )
        );
    }

    public parseLegalInformations(files: File[]): Observable<IInformationLegal[]> {
        const length = files.length;
        return range(0, length).pipe(
            map((index) => files[index]),
            switchMap((file) => this.readFile(file)),
            map((payload) => this.parseDelimitedCsv(payload)),
            map((informations: any[]) =>
                informations.map((information) => ({
                    organisation: information.o || '',
                    country: information.c || '',
                    orgUnit: information.OrgCode || '',
                    language: information.language || '',
                    legalInformation: information.legal_information || '',
                    company: information.company || '',
                    companySign: information.company_sign || '',
                    internet: information.Internet || '',
                    complianceSentence: information.complianceSentence || '',
                    clientId: information.clientID || ''
                }))
            )
        );
    }

    public parseLocationInformations(files: File[]): Observable<IInformationLocation[]> {
        const length = files.length;
        return range(0, length).pipe(
            map((index) => files[index]),
            switchMap((file) => this.readFile(file)),
            map((payload) => this.parseDelimitedCsv(payload)),
            map((informations: any[]) =>
                informations.map((information) => ({
                    organisation: information.o,

                    country: information.c || '',
                    orgUnit: information.OrgCode || '',
                    language: information.language || '',
                    location: information.location || '',
                    locationContact: information.locationcontact || '',
                    locationAddress: information.locationaddress || '',
                    senderAddress: information.senderaddress || '',
                    street: information.Street || '',
                    street2: information.Street2 || '',
                    streetPostalCode: information.StreetPostalCode || '',
                    streetLocality: information.StreetLocality || '',
                    phoneNumberExchange: information.PhoneNumberExchange || '',
                    faxNumberExchange: information.FaxNumberExchange || '',
                    countryLong: information.countryLong || ''
                }))
            )
        );
    }

    public parseTranslationInformations(files: File[]): Observable<IInformationTranslation[]> {
        const length = files.length;
        return range(0, length).pipe(
            map((index) => files[index]),
            switchMap((file) => this.readFile(file)),
            map((payload) => this.parseDelimitedCsv(payload)),
            map((informations: any[]) =>
                informations.reduce((agg, information) => {
                    const keys = Object.keys(information).filter((key) => key !== 'selected_language');

                    return [
                        ...agg,
                        ...keys
                            .map((key) => ({
                                name: information.selected_language,
                                language: key.toLowerCase(),
                                text: information[key]
                            }))
                            .filter((translation) => translation.text !== '')
                    ];
                }, [])
            )
        );
    }

    public submitManagementData(file: File): Observable<void> {
        return this.uploadFileToS3Bucket(InformationType.Management, file);
    }

    public submitLocationData(file: File): Observable<void> {
        return this.uploadFileToS3Bucket(InformationType.Location, file);
    }

    public submitLegalData(file: File): Observable<void> {
        return this.uploadFileToS3Bucket(InformationType.Legal, file);
    }

    public submitTranslationData(file: Blob): Observable<void> {
        return this.http
            .get<ISignedURL>(`${environment.gatewayAPI}/translations/upload-link`)
            .pipe(switchMap((signedURLResponse) => this.http.put<void>(signedURLResponse.signedURL, file)));
    }

    private uploadFileToS3Bucket(type: InformationType, file: File): Observable<void> {
        const fileName = encodeURIComponent(file.name.replace(/\.csv$/i, ''));
        return this.http
            .get<SignedURLDto>(`${environment.gatewayAPI}/information/${type}/upload-links/${fileName}`)
            .pipe(
                switchMap(({ signedURL }) => {
                    return this.http.put<void>(signedURL, file);
                })
            );
    }

    private readFile(blob: Blob): Observable<string> {
        if (!(blob instanceof Blob)) {
            return throwError(new Error('`blob` must be an instance of File or Blob.'));
        }

        return new Observable((obs: Observer<string>) => {
            const reader = new FileReader();

            reader.onerror = (err) => obs.error(err);
            reader.onabort = (err) => obs.error(err);
            reader.onload = () => {
                const buffer = reader.result as ArrayBuffer;
                const bytes = new Uint8Array(buffer);
                const encoding =
                    bytes[0] === 0xff && bytes[1] === 0xfe
                        ? 'utf-16le'
                        : bytes[0] === 0xfe && bytes[1] === 0xff
                          ? 'utf-16be'
                          : 'utf-8';

                obs.next(new TextDecoder(encoding).decode(buffer));
            };
            reader.onloadend = () => obs.complete();

            return reader.readAsArrayBuffer(blob);
        });
    }

    private parseDelimitedCsv(payload: string): Record<string, string>[] {
        const delimiter = '#!#';
        const lines = payload
            .replace(/^\uFEFF/, '')
            .split(/\r\n|\n|\r/)
            .filter((line) => line.trim().length > 0);

        if (lines.length === 0) {
            return [];
        }

        const headers = lines[0].split(delimiter);

        return lines.slice(1).map((line) => {
            const values = line.split(delimiter);
            return headers.reduce(
                (acc, header, index) => {
                    acc[header] = values[index] ?? '';
                    return acc;
                },
                {} as Record<string, string>
            );
        });
    }
}
